import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { LuActivity, LuBookOpen, LuCommand, LuFlame, LuListChecks, LuNewspaper, LuSearch, LuTrophy } from 'react-icons/lu';
import { Brand } from './Brand';
import { ThemeToggle } from './ThemeToggle';
import { UserMenu } from './UserMenu';
import { usePalette } from './paletteStore';
import { Kbd } from '../ui/Labels';
import { useStrings } from '../tenant/TenantProvider';
import { useFeatureEnabled } from '../tenant/useTenantSettings';
import { useCatalog, liveRegisteredContest } from '../stores/catalog';
import { useNow } from '../hooks/useNow';
import { formatCountdown } from '../lib/format';
import { cx } from '../ui/cx';

const CONTEST_REFRESH_MS = 60_000;
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

const NAV_ITEMS = [
  { to: '/problems', icon: LuListChecks, label: 'nav.problems' },
  { to: '/contests', icon: LuTrophy, label: 'nav.contests' },
  { to: '/courses', icon: LuBookOpen, label: 'nav.courses' },
  { to: '/blog', icon: LuNewspaper, label: 'nav.blog', feature: 'blogs' },
  { to: '/tracker', icon: LuActivity, label: 'nav.tracker' },
];

function AppNav() {
  const t = useStrings();
  const { pathname } = useLocation();
  const navRef = useRef(null);
  const [indicator, setIndicator] = useState(null);
  const [animated, setAnimated] = useState(false);
  const blogsEnabled = useFeatureEnabled('blogs');
  const items = NAV_ITEMS.filter((item) => !item.feature || (item.feature === 'blogs' && blogsEnabled));

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return undefined;
    let frame = 0;
    const measure = () => {
      const active = nav.querySelector('.nav-link.active');
      setIndicator(active ? { x: active.offsetLeft, width: active.offsetWidth } : null);
    };
    measure();
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    });
    observer.observe(nav);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [pathname, items.length]);

  useEffect(() => {
    if (!indicator || animated) return undefined;
    const frame = requestAnimationFrame(() => setAnimated(true));
    return () => cancelAnimationFrame(frame);
  }, [indicator, animated]);

  return (
    <nav ref={navRef} className="app-nav" aria-label={t('nav.primary')}>
      <span
        className={cx('app-nav__indicator', animated && 'is-animated')}
        aria-hidden="true"
        style={indicator ? { width: indicator.width, transform: `translateX(${indicator.x}px)` } : { opacity: 0 }}
      />
      {items.map(({ to, icon: Icon, label }) => (
        <NavLink key={to} to={to} className="nav-link" viewTransition>
          <Icon aria-hidden="true" />
          <span className="nav-link__label">{t(label)}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function SearchTrigger() {
  const t = useStrings();
  const setOpen = usePalette((state) => state.setOpen);
  return (
    <button type="button" className="search-trigger" onClick={() => setOpen(true)} aria-haspopup="dialog">
      <LuSearch aria-hidden="true" />
      <span className="search-trigger__label">{t('palette.trigger')}</span>
      <span className="search-trigger__keys" aria-hidden="true">
        <Kbd>{IS_MAC ? <LuCommand /> : 'Ctrl'}</Kbd>
        <Kbd>K</Kbd>
      </span>
    </button>
  );
}

function LiveChip() {
  const t = useStrings();
  const now = useNow(1000);
  const contests = useCatalog((state) => state.contests.data);
  const contest = liveRegisteredContest(contests, now);
  if (!contest) return null;
  const remaining = Date.parse(contest.endTime) - now;
  const label = t('nav.liveBanner', { name: contest.name });
  return (
    <Link to={`/contests/${contest.id}/arena`} className="header-chip header-chip--live" title={label}>
      <span className="header-chip__dot" aria-hidden="true" />
      <span className="header-chip__label">{t('nav.live')}</span>
      <span className="mono">{formatCountdown(remaining)}</span>
      <span className="visually-hidden">
        {label}. {t('nav.liveBannerAction')}
      </span>
    </Link>
  );
}

function StreakChip() {
  const t = useStrings();
  const daily = useCatalog((state) => state.daily.data);
  if (!daily) return null;
  const solvedToday = daily.calendar.some((entry) => entry.date === daily.todayIso && entry.solved);
  const label =
    solvedToday || daily.streak === 0 || !daily.today
      ? t('nav.streak', { count: daily.streak })
      : t('nav.streakAtRisk', { count: daily.streak });
  return (
    <span className={cx('header-chip header-chip--streak', solvedToday && 'is-active')} title={label}>
      <LuFlame aria-hidden="true" />
      <span className="tabular" aria-hidden="true">
        {daily.streak}
      </span>
      <span className="visually-hidden">{label}</span>
    </span>
  );
}

export function AppShell() {
  const t = useStrings();

  useEffect(() => {
    const { loadContests, loadDaily } = useCatalog.getState();
    loadContests();
    loadDaily();
    const timer = setInterval(() => useCatalog.getState().loadContests(), CONTEST_REFRESH_MS);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="app">
      <a href="#main" className="skip-link">
        {t('nav.skipToContent')}
      </a>
      <header className="app-header">
        <div className="app-header__inner">
          <Link to="/problems" className="brand" viewTransition>
            <Brand />
          </Link>
          <AppNav />
          <div className="app-header__actions">
            <SearchTrigger />
            <LiveChip />
            <StreakChip />
            <ThemeToggle />
            <UserMenu />
          </div>
        </div>
      </header>
      <main className="app-main" id="main" tabIndex={-1}>
        <Outlet />
      </main>
    </div>
  );
}
